import { useState } from "react"
import { cn } from "@/lib/cn"
import type {
  AlertConditionType,
  AlertDirection,
  AlertRepeatMode,
  ChartDrawing,
  PriceAlertRecord,
} from "@/types/chartOverlays"

interface Suggestion {
  kind: ChartDrawing["kind"]
  price: number
  origin_time: string | null
  label: string
  source: ChartDrawing["source"]
}

interface Props {
  symbol: string
  currentPrice: number | null
  alerts: PriceAlertRecord[]
  suggestions: Suggestion[]
  showSuggestions: boolean
  onCreateAlert: (input: {
    condition_type: AlertConditionType
    direction: AlertDirection
    threshold: number
    repeat_mode?: AlertRepeatMode
  }) => Promise<void>
  onUpdateAlert: (alertId: number, patch: Partial<PriceAlertRecord>) => Promise<void>
  onRemoveAlert: (alertId: number) => Promise<void>
  onAcceptSuggestion: (suggestion: Suggestion) => Promise<void>
  onDismissSuggestion: (suggestion: Suggestion) => void
}

export function ChartAlertPanel({
  symbol,
  currentPrice,
  alerts,
  suggestions,
  showSuggestions,
  onCreateAlert,
  onUpdateAlert,
  onRemoveAlert,
  onAcceptSuggestion,
  onDismissSuggestion,
}: Props) {
  const [conditionType, setConditionType] = useState<AlertConditionType>("level_cross")
  const [direction, setDirection] = useState<AlertDirection>("above")
  const [threshold, setThreshold] = useState(String(currentPrice ?? ""))
  const [repeatMode, setRepeatMode] = useState<AlertRepeatMode>("every_crossing")

  const parsedThreshold = Number(threshold)
  const valid = Number.isFinite(parsedThreshold) && parsedThreshold > 0

  return (
    <div className="mt-4 rounded-xl border border-cyan-500/20 bg-slate-950/70 p-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (!valid) return
          void onCreateAlert({
            condition_type: conditionType,
            direction,
            threshold: parsedThreshold,
            repeat_mode: repeatMode,
          })
        }}
      >
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          Condition
          <select
            value={conditionType}
            onChange={(event) => setConditionType(event.target.value as AlertConditionType)}
            className="mt-1 block rounded-md border-slate-700 bg-slate-900 py-1 text-xs text-slate-200"
          >
            <option value="level_cross">Price crosses level</option>
            <option value="percent_from_prev_close">% from prior close</option>
          </select>
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          When {symbol}
          <select
            value={direction}
            onChange={(event) => setDirection(event.target.value as AlertDirection)}
            className="mt-1 block rounded-md border-slate-700 bg-slate-900 py-1 text-xs text-slate-200"
          >
            <option value="above">{conditionType === "percent_from_prev_close" ? "Rises" : "Crosses above"}</option>
            <option value="below">{conditionType === "percent_from_prev_close" ? "Falls" : "Crosses below"}</option>
          </select>
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          {conditionType === "percent_from_prev_close" ? "Percent" : "Price"}
          <input
            type="number"
            min="0"
            step="any"
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
            className="mt-1 block w-32 rounded-md border-slate-700 bg-slate-900 py-1 text-xs text-slate-100"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          Repeat
          <select
            value={repeatMode}
            onChange={(event) => setRepeatMode(event.target.value as AlertRepeatMode)}
            className="mt-1 block rounded-md border-slate-700 bg-slate-900 py-1 text-xs text-slate-200"
          >
            <option value="every_crossing">Every crossing</option>
            <option value="once">Once</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={!valid}
          className="rounded-md bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-40"
        >
          Create alert
        </button>
        <a href="/price_alerts" className="pb-1 text-[10px] text-cyan-300 hover:text-cyan-200">
          Manage all alerts
        </a>
        <span className="pb-1 text-[10px] text-slate-500">In-app + email unless turned off</span>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((suggestion) => (
            <li
              key={`${suggestion.kind}-${suggestion.price}`}
              className="flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-2 py-1 text-[11px] text-amber-100"
            >
              {suggestion.label} {formatPrice(suggestion.price)}
              <button type="button" onClick={() => void onAcceptSuggestion(suggestion)} className="text-amber-300">
                Accept
              </button>
              <button type="button" onClick={() => onDismissSuggestion(suggestion)} className="text-slate-500">
                Dismiss
              </button>
            </li>
          ))}
        </ul>
      )}

      {alerts.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {alerts.map((alert) => (
            <li
              key={alert.id}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1 text-[11px]",
                alert.active ? "bg-slate-800 text-slate-300" : "bg-slate-900 text-slate-500",
              )}
            >
              {alert.description}
              <button
                type="button"
                onClick={() => void onUpdateAlert(alert.id, { active: !alert.active })}
                className="text-cyan-300"
              >
                {alert.active ? "Pause" : "Resume"}
              </button>
              <button
                type="button"
                aria-label={`Delete alert ${alert.description}`}
                onClick={() => void onRemoveAlert(alert.id)}
                className="text-slate-500 hover:text-rose-300"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function formatPrice(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 })
}
